import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { UnionDomain } from 'src/app/services/union.domain';
import { SubdomainService } from 'src/app/services/subdomain.service';
import { Location } from '@angular/common';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';

// Define an interface for tax collection receipt
interface TaxReceipt {
  id: number;
  receipt_number: string;
  name: string;
  holding_number: string;
  ward_number: number;
  gram_or_rasta: string;
  financial_year: string;
  tax_amount: string;
  collection_date: string;
  payment_method?: string;
  collector_name?: string;
  arrears?: string;
  fine?: string;
  discount?: string;
  qr_code?: string;
  gurdian_name?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-tax-collection-receipt',
  templateUrl: './tax-collection-receipt.component.html',
  styleUrls: ['./tax-collection-receipt.component.scss']
})
export class TaxCollectionReceiptComponent implements OnInit {
  receipt: TaxReceipt = {
    id: 0,
    receipt_number: '',
    name: '',
    holding_number: '',
    ward_number: 0,
    gram_or_rasta: '',
    financial_year: '',
    tax_amount: '',
    collection_date: '',
    gurdian_name: ''
  };
  
  unionLogo: string = '';
  userName: string = '';
  currentYear: number = new Date().getFullYear();
  isLoading: boolean = false;
  amountInWords: string = '';
  private apiUrl = environment.apiBaseUrl;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    public unionDomain: UnionDomain,
    private subdomainService: SubdomainService,
    private location: Location,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    // Use the Bangladesh government logo instead of union-specific logo
    this.unionLogo = '../../../../assets/img/logo.png';
    this.userName = localStorage.getItem('name') || 'Admin';
    
    // Get the receipt ID from the route
    this.route.queryParams.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.loadReceiptData(id);
      } else {
        // Load demo data if no ID is provided
        this.loadDemoData();
      }
    });
    
    // Initialize amount in words based on default values
    this.setAmountInWords();
  }

  // Load receipt data from API
  loadReceiptData(id: string): void {
    this.isLoading = true;
    this.http.get<any>(`${this.apiUrl}/api/taxCollection/get/${id}`).subscribe({
      next: (response) => {
        this.receipt = response;
        this.isLoading = false;
        // Set the amount in words
        this.setAmountInWords();
      },
      error: (error) => {
        console.error('Failed to load receipt data:', error);
        this.toastr.error('Failed to load receipt data');
        this.isLoading = false;
        this.loadDemoData(); // Load demo data on error
      }
    });
  }

  // Load demo data for development/preview
  loadDemoData(): void {
    this.receipt = {
      id: 1,
      receipt_number: 'TX-' + Math.floor(100000 + Math.random() * 900000),
      name: 'আব্দুল করিম',
      holding_number: '০০১/০১',
      ward_number: 1,
      gram_or_rasta: 'পূর্ব পাড়া',
      financial_year: '২০২৩-২০২৪',
      tax_amount: '৫০০',
      collection_date: new Date().toISOString(),
      payment_method: 'নগদ',
      collector_name: this.userName,
      arrears: '১০০',
      fine: '৫০',
      discount: '৫০',
      qr_code: '00101234567',
      gurdian_name: 'ইউসুফ আলী'
    };
    
    // Set the amount in words
    this.setAmountInWords();
  }

  // Calculate total amount
  calculateTotal(): string {
    let total = parseInt(this.receipt.tax_amount || '0');
    
    if (this.receipt.arrears) {
      total += parseInt(this.receipt.arrears);
    }
    
    if (this.receipt.fine) {
      total += parseInt(this.receipt.fine);
    }
    
    if (this.receipt.discount) {
      total -= parseInt(this.receipt.discount);
    }
    
    return total.toString();
  }
  
  // Convert number to Bengali words
  setAmountInWords(): void {
    const total = this.calculateNumericTotal();
    
    // Simple mapping for demonstration - in a real app, you might want a more complete number to word conversion
    const numberWords: { [key: number]: string } = {
      0: 'শূন্য',
      1: 'এক',
      2: 'দুই',
      3: 'তিন',
      4: 'চার',
      5: 'পাঁচ',
      6: 'ছয়',
      7: 'সাত',
      8: 'আট',
      9: 'নয়',
      10: 'দশ',
      100: 'একশত',
      500: 'পাঁচশত',
      600: 'ছয়শত',
      700: 'সাতশত',
      800: 'আটশত',
      900: 'নয়শত'
    };
    
    // For simplicity, just handle up to 900
    if (total <= 900 && numberWords[total]) {
      this.amountInWords = numberWords[total];
    } else if (total > 900) {
      this.amountInWords = 'নয়শত এবং ' + (numberWords[total - 900] || (total - 900).toString());
    } else {
      this.amountInWords = total.toString();
    }
  }
  
  // Calculate total without setting amountInWords (to avoid infinite recursion)
  private calculateNumericTotal(): number {
    let total = parseInt(this.receipt.tax_amount || '0');
    
    if (this.receipt.arrears) {
      total += parseInt(this.receipt.arrears);
    }
    
    if (this.receipt.fine) {
      total += parseInt(this.receipt.fine);
    }
    
    if (this.receipt.discount) {
      total -= parseInt(this.receipt.discount);
    }
    
    return total;
  }

  // Go back to previous page
  goBack(): void {
    this.location.back();
  }

  // Print the receipt
  printReceipt(): void {
    window.print();
  }
}
